import { SingletonService } from '@/common/dependency.configs'
import { inject, injectable } from 'inversify'
import HttpHandler from '@/common/services/connect/HttpHandler'
import SERVICE_PATH_CONSTANTS from '@/common/constant/servicePathConstants'

const LOG_PATH = `${SERVICE_PATH_CONSTANTS.BOLTSUI}/log`

interface LogContext {
  institutionId: string
  userAgent: string
  windowWidth: number
  windowHeight: number
  route: string
  timestamp: string
}

/**
 * The payload sent to POST /log.
 *
 * Why appChannel and level are plain strings, not enums or union types:
 * -----------------------------------------------------------------------
 * The backend maps unrecognized channel values to "unknown" and unrecognized level
 * values to WARN rather than rejecting the request. This means a new channel or
 * level can be added on either side without requiring a coordinated release of
 * both the frontend and backend at the same time.
 */
interface LogRequest {
  appChannel: string
  level: string
  message: string
  context: LogContext
}

interface ConfigureOptions {
  appChannel: string
  institutionId: string
}

@SingletonService
@injectable()
class LoggingService {
  private appChannel: string | null = null
  private institutionId = ''

  constructor(
    @inject(HttpHandler) private httpHandler: HttpHandler,
  ) {}

  configure(options: ConfigureOptions): void {
    this.appChannel = options.appChannel
    this.institutionId = options.institutionId
  }

  debug(message: string): void {
    this.send('debug', message)
  }

  info(message: string): void {
    this.send('info', message)
  }

  warn(message: string): void {
    this.send('warn', message)
  }

  error(message: string): void {
    this.send('error', message)
  }

  private send(level: string, message: string): void {
    // Silently drop entries until configure() has been called. This guards against
    // code that logs before the app channel is known.
    if (!this.appChannel) return

    this.httpHandler.post(LOG_PATH, this.buildPayload(level, message)).catch(() => {})
  }

  private buildPayload(level: string, message: string): LogRequest {
    return {
      appChannel: this.appChannel!,
      level,
      message,
      context: this.buildContext(),
    }
  }

  private buildContext(): LogContext {
    return {
      institutionId: this.institutionId,
      userAgent: window.navigator.userAgent,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      route: window.location.pathname ?? '',
      timestamp: new Date().toISOString(),
    }
  }
}

export default LoggingService
